import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';

import { showError, getCleanedUrlParams } from '@utils';
import { QUERY_KEYS } from 'constants/queryKeys';
import { availabilityServices } from '@services';
import { handleAvailability, handleEnums } from 'hooks/availabilityTable/utils';

import {
  getForSaleQueryParams,
  getForSaleRequestPayload,
  FOR_SALE_PAGE_SIZE,
} from './utils';

export const useForSaleVehiclesOperations = () => {
  const { query, isReady } = useRouter();
  const {
    page = 1,
    pageSize = FOR_SALE_PAGE_SIZE,
    orderBy = '',
    order = '',
    ...remainingQueries
  } = query;

  const cleanParams = getCleanedUrlParams({
    ...remainingQueries,
    page,
    orderBy,
    order,
    pageSize: parseInt(pageSize, 10) || FOR_SALE_PAGE_SIZE,
  });

  const {
    data: availabilityEnumsData,
    isLoading: isAvailabilityEnumsLoading,
    isError: isAvailabilityEnumsError,
    error: availabilityEnumsError,
    isFetched: isAvailabilityEnumsIsFetched,
  } = useQuery(
    [QUERY_KEYS.GET_AVAILABILITY_ENUMS_KEY],
    () => availabilityServices?.getAvailabilityEnums(),
    {
      enabled: !!isReady,
    }
  );

  const enums = handleEnums(availabilityEnumsData);

  const {
    data: forSaleData,
    isLoading: isForSaleLoading,
    isError: isForSaleError,
    error: forSaleError,
    refetch: refetchForSaleVehicles,
  } = useQuery(
    [QUERY_KEYS.GET_FOR_SALE_VEHICLES_KEY, { cleanParams }],
    () => {
      const params = getForSaleQueryParams(cleanParams);
      const payload = getForSaleRequestPayload(enums);

      return availabilityServices?.getAvailability({ params, payload });
    },
    {
      enabled: !!isReady && isAvailabilityEnumsIsFetched,
      refetchOnMount: false,
    }
  );

  const data = handleAvailability(forSaleData?.data || []);
  const total = forSaleData?.meta?.total;

  useEffect(() => {
    if (isForSaleError) {
      showError(forSaleError);
    }
  }, [isForSaleError]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAvailabilityEnumsError) {
      showError(availabilityEnumsError);
    }
  }, [isAvailabilityEnumsError]); // eslint-disable-line react-hooks/exhaustive-deps

  const loading = isForSaleLoading || isAvailabilityEnumsLoading;
  const enumsLoading = isAvailabilityEnumsLoading;

  return {
    loading,
    total,
    data,
    page,
    pageSize: parseInt(pageSize, 10) || FOR_SALE_PAGE_SIZE,
    enums,
    enumsLoading,
    refetchForSaleVehicles,
  };
};
